import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { trackFormelCta } from "@/lib/ga4";
import { trackInitiateCheckout } from "@/lib/metaPixel";

export const FORMEL_CHECKOUT_URL = "https://inside.effektiv-kommunizieren.de/s/AJUNG/die-4-fokustypen-formel-limited-edition_2c5fc0e7/payment";
export const FORMEL_REGULAR_URL = "https://inside.effektiv-kommunizieren.de/s/AJUNG/die-4-fokustypen-formel-d9bd6bc1/payment";
// Einfuehrungspaket endet Fr 16.10.2026, 23:59:59 Berliner Zeit (CEST = UTC+2).
export const OFFER_END = Date.UTC(2026, 9, 16, 21, 59, 59);

// Angebotsphase; QA-Vorschau des Endzustands mit ?offer=expired.
export const useOffer = () => {
  const forceExpired = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("offer") === "expired";
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);
  const left = forceExpired ? 0 : Math.max(0, OFFER_END - now);
  return { active: left > 0, left, href: left > 0 ? FORMEL_CHECKOUT_URL : FORMEL_REGULAR_URL };
};

const pad = (n) => String(n).padStart(2, "0");

export const OfferTimer = ({ left, testId, dark = false }) => {
  const s = Math.floor(left / 1000);
  const d = Math.floor(s / 86400);
  const clock = `${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
  return (
    <div className={`text-sm leading-relaxed space-y-0.5 ${dark ? "text-white/80" : "text-slate-600"}`} data-testid={testId}>
      <p className="font-bold" style={{ color: ORANGE }}>Einführungspaket zum PocketCoach-Start</p>
      <p>99 € inkl. persönlichem PocketCoach-Bonus</p>
      <p>Endet in:{" "}
        <span className={`font-bold tabular-nums ${dark ? "text-white" : ""}`} style={dark ? undefined : { color: NAVY }} data-testid={`${testId}-clock`}>
          {d > 0 ? `${d} ${d === 1 ? "Tag" : "Tage"} ` : ""}{clock}
        </span>
      </p>
    </div>
  );
};
export const ORANGE = "#EE7F35";
export const BLUE = "#3F6DB0";
export const NAVY = "#0F172A";

const onCtaClick = (position, variant, intro) => {
  trackFormelCta(position, variant, intro ? "intro" : "regular");
  trackInitiateCheckout({ value: intro ? 99 : 179, contentName: "formel", position });
};

export const Cta = ({ label, position, variant, testId, href, light = false }) => (
  <a href={href} onClick={() => onCtaClick(position, variant, href === FORMEL_CHECKOUT_URL)} data-testid={testId}
    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full px-8 py-4 text-base sm:text-lg font-extrabold shadow-lg transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0"
    style={{ backgroundColor: light ? "#fff" : ORANGE, color: light ? NAVY : "#fff" }}>
    {label} <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
  </a>
);

export const Price = ({ testId, active = true, dark = false }) => (
  <div className="flex flex-col leading-tight" data-testid={testId}>
    {active && <span className={`text-sm ${dark ? "text-white/50" : "text-slate-400"}`} data-testid={`${testId}-regular`}>statt <span className="line-through">179,00 €</span></span>}
    <span className={`text-3xl sm:text-4xl font-extrabold ${dark ? "text-white" : ""}`} style={dark ? undefined : { color: NAVY }} data-testid={`${testId}-action`}>{active ? "99,00 €" : "179,00 €"}</span>
  </div>
);

const TYPES = [
  ["katze", "Katze", "Was ist daran spannend – für mich?", "Will wissen, was interessant ist und welchen persönlichen Reiz die Sache hat.", "#FDE7D6"],
  ["eule", "Eule", "Wie hängt das alles zusammen?", "Will das große Ganze, den Zusammenhang und die Logik verstehen.", "#DCE7F7"],
  ["biene", "Biene", "Und was mache ich jetzt konkret?", "Will wissen, wie es konkret umgesetzt wird und was jetzt zu tun ist.", "#FFF1C7"],
  ["hund", "Hund", "Was bedeutet das für uns?", "Achtet auf Menschen, Beziehung, Zusammenarbeit und ein gutes Miteinander.", "#DDF0E4"],
];

const FlipCard = ({ type }) => {
  const [key, name, question, text, bg] = type;
  const [open, setOpen] = useState(false);
  const face = "absolute inset-0 rounded-3xl p-4 sm:p-5 flex flex-col items-center justify-center text-center";
  return (
    <button type="button" onClick={() => setOpen(!open)} data-testid={`formel-type-${key}`} aria-pressed={open}
      className="relative h-56 sm:h-64 w-full [perspective:1000px] focus:outline-none">
      <div className="relative h-full w-full transition-transform duration-500 [transform-style:preserve-3d]" style={{ transform: open ? "rotateY(180deg)" : "none" }}>
        <div className={`${face} [backface-visibility:hidden] shadow-md`} style={{ backgroundColor: bg }}>
          <img src={`/icons/icon-${key}.png`} alt="" className="w-20 h-20 sm:w-24 sm:h-24 mb-3" />
          <p className="text-xl font-extrabold" style={{ color: NAVY }}>{name}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-500">Antippen</p>
        </div>
        <div className={`${face} [backface-visibility:hidden] [transform:rotateY(180deg)] shadow-md bg-white border-2`} style={{ borderColor: bg }}>
          <p className="text-sm sm:text-base font-extrabold mb-1.5 leading-snug" style={{ color: BLUE }} data-testid={`formel-type-${key}-question`}>„{question}“</p>
          <p className="text-xs sm:text-sm text-slate-700 leading-snug">{text}</p>
        </div>
      </div>
    </button>
  );
};

export const TypeFlipGrid = () => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="formel-types">
    {TYPES.map((t) => <FlipCard key={t[0]} type={t} />)}
  </div>
);
